import React, { useEffect, useState } from "react";
import {
  Card,
  CardBody,
  CardTitle,
  Col,
  Row,
  Form,
  FormGroup,
  Label,
  Input,
  Button,
  Spinner,
  Table,
} from "reactstrap";
import { useNavigate, NavLink } from "react-router-dom";
import axiosInstance from "../../../middleware/axiosInstance";
import Select from "react-select";
import { BiSearch } from "react-icons/bi";


const Attendance = () => {
  const navigate = useNavigate();

  const [formData, setFormData] = useState({
    branchId: null,
    classId: null,
    attendanceDate: new Date().toISOString().split("T")[0],
    academicYearId: null,
    sectionId: null,
    reportType: "day_wise",
    month: null,
    years: null,
    isDayWise: true,
    isMonthWise: false,
  });

  const [loading, setLoading] = useState(false);
  const [branches, setBranches] = useState([]);
  const [classes, setClasses] = useState([]);
  const [academicYears, setAcademicYears] = useState([]);
  const [sections, setSections] = useState([]);
  const [error, setError] = useState("");
  const [attendance, setAttendance] = useState([]);
  const [noDataFound, setNoDataFound] = useState(false);
  const [yearsOptions, setYearsOptions] = useState([]);


  const monthOptions = [
    { value: 0, label: "January" },
    { value: 1, label: "February" },
    { value: 2, label: "March" },
    { value: 3, label: "April" },
    { value: 4, label: "May" },
    { value: 5, label: "June" },
    { value: 6, label: "July" },
    { value: 7, label: "August" },
    { value: 8, label: "September" },
    { value: 9, label: "October" },
    { value: 10, label: "November" },
    { value: 11, label: "December" },
  ];

  // Report Type options
  const reportTypeOptions = [
    { value: "day_wise", label: "Day Wise" },
    { value: "month_wise", label: "Month Wise" },
  ];

  useEffect(() => {
    const getAcademicYears = async () => {
      try {
        const response = await axiosInstance.get("/academicyears");

        const sortedAcademicYears = response.data.sort((a, b) => {
          // Extract starting years from "year" (e.g., "2024-25" => 2024)
          const startYearA = parseInt(a.year.split("-")[0]);
          const startYearB = parseInt(b.year.split("-")[0]);

          // Sort based on the starting year
          return startYearA - startYearB;
        });

        setAcademicYears(sortedAcademicYears);

        const currentDate = new Date();
        const currentYear = currentDate.getFullYear();
        const currentMonth = currentDate.getMonth();
        const currentDay = currentDate.getDate();

        let defaultAcademicYear;

        if (currentMonth > 3) {
          defaultAcademicYear = `${currentYear}-${String(currentYear + 1).slice(
            -2
          )}`;
        } else {
          defaultAcademicYear = `${currentYear - 1}-${String(currentYear).slice(
            -2
          )}`;
        }

        const defaultAcademicYearEntry = response.data.find(
          (year) => year.year === defaultAcademicYear
        );

        if (defaultAcademicYearEntry) {
          setFormData((prevFormData) => ({
            ...prevFormData,
            academicYearId: defaultAcademicYearEntry._id,
          }));
          const yearRange = defaultAcademicYearEntry?.year?.split("-");
          const startYear = parseInt(yearRange[0], 10);
          const endYear = yearRange[1] ? parseInt(`20${yearRange[1]}`, 10) : startYear + 1;
          let yearsOptions = [
            { value: startYear, label: `${startYear}` },
            { value: endYear, label: `${endYear}` },
          ];
          setYearsOptions(yearsOptions);
        }
      } catch (error) {
        console.error("Error fetching academic years:", error);
      } finally {
        setLoading(false);
      }
    };

    getAcademicYears();
  }, []);

  // Fetch branches
  const fetchBranches = async () => {
    try {
      const response = await axiosInstance.get("/branches");
      setBranches(
        response.data.map((branch) => ({
          value: branch._id,
          label: branch.branchName,
        }))
      );
    } catch (error) {
      console.error("Error fetching branches:", error);
    }
  };

  // Fetch classes based on selected branch
  const fetchClasses = async () => {
    if (!formData.branchId) return;

    try {
      const response = await axiosInstance.get("/classes/branch", {
        params: { branchId: formData.branchId },
      });
      setClasses(
        response.data.map((classObj) => ({
          value: classObj._id,
          label: classObj.className,
        }))
      );
    } catch (error) {
      console.error("Error fetching classes:", error);
    }
  };

  const fetchSections = async () => {
    if (formData.branchId && formData.classId) {
      try {
        const response = await axiosInstance.get("/sections", {
          params: {
            branchId: formData.branchId,
            classId: formData.classId,
          },
        });
        const sortedSections = response.data.sort((a, b) =>
          a.sectionName.localeCompare(b.sectionName)
        );
        setSections(sortedSections);
      } catch (error) {
        setError("Failed to fetch sections.");
      }
    } else {
      setSections([]);
    }
  };

  useEffect(() => {
    if (formData.branchId) {
      fetchClasses();
    } else {
      setClasses([]);
      setFormData((prev) => ({
        ...prev,
        classId: null,
      }));
    }
  }, [formData.branchId]);

  useEffect(() => {
    if (formData.academicYearId) {
      setBranches([]); // Clear branches when academic year changes
      setClasses([]); // Clear classes
      setSections([]); // Clear sections
      setFormData((prev) => ({
        ...prev,
        branchId: "", // Reset branch
        classId: "", // Reset class
        sectionId: "", // Reset section
      }));
      fetchBranches(); // Fetch branches for the new academic year
    }
  }, [formData.academicYearId]);

  useEffect(() => {
    if (formData.branchId && formData.classId) {
      fetchSections(formData.branchId, formData.classId);
    } else {
      setSections([]); // Clear sections if either branchId or classId is missing
    }
  }, [formData.branchId, formData.classId]);



  const handleSelectChange = (selectedOption, { name }) => {
    setFormData((prev) => {
      if (name === "reportType") {
        setAttendance([]);

        return {
          academicYearId: prev.academicYearId,
          attendanceDate: new Date().toISOString().split("T")[0],
          reportType: selectedOption ? selectedOption.value : null,
          isDayWise: selectedOption?.value === "day_wise", // Set isDayWise if selected
          isMonthWise: selectedOption?.value === "month_wise", // Set isMonthWise if selected
        };
      }

      if (
        ["branchId", "classId", "sectionId", "years", "month"].includes(name)
      ) {
        setAttendance([]);
      }

      return {
        ...prev,
        [name]: selectedOption ? selectedOption.value : null,
        ...(name === "branchId" && { classId: null }),
      };
    });
  };

  const handleChange = (e) => {
    const { name, value } = e.target;

    // Reset attendance array when the academic year changes
    if (name === "academicYearId") {
      setAttendance([]);
      const selectedYear = academicYears?.find((year)=> year?._id == value)
      if (selectedYear) {
        const yearRange = selectedYear?.year?.split("-");
        const startYear = parseInt(yearRange[0], 10);
        const endYear = yearRange[1] ? parseInt(`20${yearRange[1]}`, 10) : startYear + 1;
        let yearsOptions = [
          { value: startYear, label: `${startYear}` },
          { value: endYear, label: `${endYear}` },
        ];
        setYearsOptions(yearsOptions);
      }
    }

    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  const handleSearch = async (e) => {
    e.preventDefault();

    try {
      setLoading(true);
      const response = await axiosInstance.get("/attendance", {
        params: formData,
      }); // Send formData as query params
      // console.log("data", response.data);

      setAttendance(response.data);
      setNoDataFound(response.data.length === 0);
    } catch (error) {
      console.error("Error importing attendance:", error);
      setError("Failed to import attendance. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  // Custom styles for react-select
  const customStyles = {
    placeholder: (provided) => ({
      ...provided,
      whiteSpace: "nowrap",
      textOverflow: "ellipsis",
      overflow: "hidden",
    }),
    control: (provided, state) => ({
      ...provided,
      borderColor: state.isFocused ? "#FB0F01" : provided.borderColor,
      boxShadow: state.isFocused ? "0 0 0 1px #FB0F01" : null,
      borderWidth: state.isFocused ? "0px" : "1px",
      transition: "border-color 0.2s, box-shadow 0.2s",
      "&:hover": {
        borderColor: "#FB0F01",
      },
    }),
    menu: (provided) => ({
      ...provided,
      backgroundColor: "#FFFFFF",
      zIndex: 999,
    }),
    option: (provided, state) => ({
      ...provided,
      backgroundColor: state.isSelected ? "#FDD7C2" : "#FFFFFF",
      color: "black",
      "&:hover": {
        backgroundColor: "#FDD7C2",
        color: "black",
      },
      "&:active": {
        backgroundColor: "#E8F0FE",
      },
    }),
  };


  const renderTable = () => {
    // Function to return color based on status
    const getStatusColor = (status) => {
      switch (status) {
        case "L":
          return "orange"; // Late
        case "H":
          return "#969393"; // Holiday
        case "A":
          return "#FB0F01"; // Absent
        case "P":
          return "#008000"; // Present
        default:
          return "black"; // No Attendance
      }
    };

    if (formData.isDayWise) {
      return (
        <div>
          {attendance.length > 0 && (
            <>
              <div className="px-0 pb-3 d-flex flex-wrap justify-content-between">
                <CardTitle tag="h3" className="mt-4 mb-1">
                  Daily Attendance Report
                </CardTitle>

                <div className=" d-flex flex-wrap  mt-4 px-4 py-2 rounded" style={{ backgroundColor: "#f3f4f6" }}>
                  <div className="px-2 py-1 rounded">
                    Present = <span style={{ color: "#008000", fontWeight: "bold" }}>P</span>
                  </div>
                  <div className="px-2 py-1 rounded">
                    Absent = <span style={{ color: "#FB0F01", fontWeight: "bold" }}>A</span>
                  </div>
                  <div className="px-2 py-1 rounded">
                    Late = <span style={{ color: "orange", fontWeight: "bold" }}>L</span>
                  </div>
                  <div className="px-2 py-1 rounded">
                    Holiday = <span style={{ color: "#969393", fontWeight: "bold" }}>H</span>
                  </div>
                </div>

              </div>
              <div style={{ overflowX: "auto" }}>
                <Table bordered striped responsive className="mb-0">
                  <thead
                    style={{ backgroundColor: "#f8f9fa", color: "#495057" }}
                  >
                    <tr>

                      <th>Roll No</th>
                      <th>Student Name</th>
                      <th>Status</th>
                    </tr>
                  </thead>
                  <tbody>
                    {attendance.map((val) => (
                      <React.Fragment key={val._id}>
                        {val.students.map((student) => (
                          <tr key={student.student_id._id}>
                            <td>{student.roll}</td>
                            <td>{`${student.firstName} ${student.lastName}`}</td>
                            <td
                              style={{ color: getStatusColor(student.status), fontWeight: "bold" }}
                            >
                              {student.status}
                            </td>
                          </tr>
                        ))}
                      </React.Fragment>
                    ))}
                  </tbody>
                </Table>
              </div>
            </>
          )}
        </div>
      );
    }

    if (formData.isMonthWise) {
      // Generate month-wise attendance table
      const getMonthDates = (year, month) => {
        const dates = [];
        const daysInMonth = new Date(year, month + 1, 0).getDate();
        for (let day = 1; day <= daysInMonth; day++) {
          const date = new Date(Date.UTC(year, month, day));
          date.setUTCHours(0, 0, 0, 0);
          dates.push(date.toISOString());
        }
        return dates;
      };

      const monthIndex = Number(formData.month);
      const monthYear = Number(formData.years);
      const monthDates = getMonthDates(monthYear, monthIndex);

      const studentAttendance = {};
      attendance.forEach((record) => {
        record.students.forEach((student) => {
          let studentObj = student.student_id;
          if (typeof studentObj !== "object" || !studentObj) {
            studentObj = {
              _id: student.student_id,
              name: `${student.firstName} ${student.lastName}`,
              registerNo: student.registerNo,
              roll: student.roll,
            };
          }

          if (!studentAttendance[studentObj._id]) {
            studentAttendance[studentObj._id] = {
              name: studentObj.name,
              registerNo: studentObj.registerNo,
              roll: studentObj.roll,
              branch: record.branch_id?.branchName || "Unknown Branch",
              class: record.class_id
                ? `${record.class_id.className}${record.section_id ? ` (${record.section_id.sectionName})` : ""}`
                : "N/A",
              attendance: {},
              countP: 0, // Present
              countA: 0, // Absent
              countL: 0, // Late
              countH: 0, // Holiday
            };
          }

          // studentAttendance[studentObj._id].attendance[record.attendanceDate] =
          //   student.status || "";


          const status = student.status || "";
          studentAttendance[studentObj._id].attendance[record.attendanceDate] = status;

          // Increment the respective counters
          if (status === "P") studentAttendance[studentObj._id].countP += 1;
          if (status === "A") studentAttendance[studentObj._id].countA += 1;
          if (status === "L") studentAttendance[studentObj._id].countL += 1;
          if (status === "H") studentAttendance[studentObj._id].countH += 1;

        });
      });


      // console.log('studentAttendance', studentAttendance)

      return (
        <div>
          {attendance.length > 0 && (
            <>
              <div className="px-0 pb-3 d-flex flex-wrap justify-content-between">
                <CardTitle tag="h3" className="mt-4 mb-1">
                  Monthly Attendance Report
                </CardTitle>

                <div className="flex flex-wrap gap-4 mt-4 px-4 py-2 rounded" style={{ backgroundColor: "#f3f4f6" }}>
                  <span className="px-2 py-1 rounded">
                    Present = <span style={{ color: "#008000", fontWeight: "bold" }}>P</span>
                  </span>
                  <span className="px-2 py-1 rounded">
                    Absent = <span style={{ color: "#FB0F01", fontWeight: "bold" }}>A</span>
                  </span>
                  <span className="px-2 py-1 rounded">
                    Late = <span style={{ color: "orange", fontWeight: "bold" }}>L</span>
                  </span>
                  <span className="px-2 py-1 rounded">
                    Holiday = <span style={{ color: "#969393", fontWeight: "bold" }}>H</span>
                  </span>
                </div>

              </div>

              <div style={{ overflowX: "auto" }}>
                <Table bordered striped responsive className="mb-0">
                  <thead style={{ backgroundColor: "#f8f9fa", color: "#495057", textAlign: "center" }}>
                    <tr>
                      <th rowSpan="2" style={{ verticalAlign: "middle" }}>Roll No</th>
                      <th rowSpan="2" style={{ verticalAlign: "middle" }}>Student Name</th>
                      <th rowSpan="2" style={{ verticalAlign: "middle" }}>P</th>
                      <th rowSpan="2" style={{ verticalAlign: "middle" }}>A</th>
                      <th rowSpan="2" style={{ verticalAlign: "middle" }}>L</th>
                      <th rowSpan="2" style={{ verticalAlign: "middle" }}>H</th>

                      {monthDates.map((date, index) => (
                        <th key={index} style={{ padding: "8px", borderBottom: "none" }}>
                          {new Date(date).toLocaleDateString("en-GB", { day: "2-digit" })}
                        </th>
                      ))}
                    </tr>
                    <tr>
                      {monthDates.map((date, index) => (
                        <th key={index} style={{ fontSize: "14px", color: "#6c757d", paddingBottom: "10px", borderTop: "none" }}>
                          {new Date(date).toLocaleDateString("en-GB", { weekday: "short" })}
                        </th>
                      ))}
                    </tr>
                  </thead>

                  <tbody>
                    {Object.keys(studentAttendance).map((studentId) => {
                      const student = studentAttendance[studentId];
                      return (
                        <tr key={studentId}>
                          <td>{student.roll}</td>
                          <td>{student.name}</td>
                          <td style={{ fontWeight: "bold" }}>{student.countP}</td>
                          <td style={{ fontWeight: "bold" }}>{student.countA}</td>
                          <td style={{ fontWeight: "bold" }}>{student.countL}</td>
                          <td style={{ fontWeight: "bold" }}>{student.countH}</td>
                          {monthDates.map((date, index) => {
                            const status = student.attendance[date] || "";
                            const color = getStatusColor(status);
                            return (
                              <td key={index} style={{ color: color, fontWeight: "bold" }}>
                                {status} {/* Shows only L, H, A, P */}
                              </td>
                            )
                          })}
                        </tr>
                      );
                    })}
                  </tbody>
                </Table>
                <style jsx="true">{`
      table td {
        white-space: nowrap;
      }
      table th {
        white-space: nowrap;
      }

      @media (max-width: 768px) {
        table td,
        table th {
          padding: 5px;
          white-space: nowrap;
        }
      }

      @media (max-width: 576px) {
        table td,
        table th {
          white-space: nowrap;
        }
      }
    `}</style>
              </div>
            </>
          )}
        </div>
      );
    }

    return null; // If neither is true, return nothing
  };

  return (
    <>
          <Card
            style={{
              boxShadow: "none",
              marginBottom: "10px",
              borderRadius: "0",
            }}
          >
            <div className="d-sm-flex justify-content-between align-items-center px-3 pb-1 pt-1">
              <CardTitle className="mt-2" tag="h4">
            Attendance
          </CardTitle>

          <div className="d-sm-flex  align-items-center mb-1 mb-sm-0 ">
          <style>
        {`
          .responsive-div {
            min-width: 200px;
          }

          @media (max-width: 576px) {
            .responsive-div {
              width: 100%;
            }
          }
        `}
      </style>

      <div className="pb-0 mb-0 me-3 mb-1 mb-sm-0 responsive-div">
              <Select
                name="reportType"
                options={reportTypeOptions}
                value={
                  reportTypeOptions.find(
                    (t) => t.value === formData.reportType
                  ) || null
                }
                onChange={handleSelectChange}
                isClearable={false}
                placeholder="Select Report Type"
                styles={customStyles}
              />
            </div>
            
            <NavLink to="/attendance/import" className="pb-0 mb-0">
              <Button className="btn-no-radius" color="primary" style={{width:'100%'}}>
                Import Attendance
              </Button>
            </NavLink>
          </div>
        </div>
       
        <hr className="mt-0" />
        {formData.reportType && (
          <div className="px-3 pt-0">
            <Form className="mt-2 mb-2" onSubmit={handleSearch}>
              <Row>
                <Col xs={12} sm={6} md={4} xl={3}>
                  <FormGroup>
                    <Label for="academicYear">
                      <span style={{ fontWeight: "bold" }}>Academic Year</span>
                    </Label>
                    <Select
                      id="academicYear"
                      options={academicYears.map((year) => ({
                        value: year._id,
                        label: year.year,
                      }))}
                      onChange={(selected) => {
                        handleChange({
                          target: {
                            name: "academicYearId",
                            value: selected ? selected.value : "", // Handle null case
                          },
                        });

                        // Reset attendance array when academic year changes
                        setAttendance([]);
                      }}
                      value={
                        academicYears.find(
                          (year) => year._id === formData.academicYearId
                        )
                          ? {
                            value: formData.academicYearId,
                            label: academicYears.find(
                              (year) => year._id === formData.academicYearId
                            ).year,
                          }
                          : null
                      }
                      placeholder="Select Academic Year"
                      isSearchable={true}
                      styles={customStyles}
                    />
                  </FormGroup>
                </Col>
                <Col xs={12} sm={6} md={4} xl={3}>
                  <FormGroup>
                    <Label>
                      <strong>Branch*</strong>
                    </Label>
                    <Select
                      name="branchId"
                      options={branches}
                      value={
                        branches.find((b) => b.value === formData.branchId) ||
                        null
                      }
                      onChange={handleSelectChange}
                      isClearable
                      placeholder="Select a Branch"
                      styles={customStyles}
                    />
                  </FormGroup>
                </Col>

                <Col xs={12} sm={6} md={4} xl={3}>
                  <FormGroup>
                    <Label>
                      <strong>Class*</strong>
                    </Label>
                    <Select
                      name="classId"
                      options={classes}
                      value={
                        classes.find((c) => c.value === formData.classId) ||
                        null
                      }
                      onChange={handleSelectChange}
                      isClearable
                      placeholder="Select a Class"
                      styles={customStyles}
                    />
                  </FormGroup>
                </Col>

                <Col xs={12} sm={6} md={4} xl={3}>
                  <FormGroup>
                    <Label for="section">
                      <span style={{ fontWeight: "bold" }}>Section</span>
                    </Label>
                    <Select
                      id="section"
                      options={sections.map((section) => ({
                        value: section._id,
                        label: section.sectionName,
                      }))}
                      onChange={(selected) => {
                        handleChange({
                          target: {
                            name: "sectionId",
                            value: selected ? selected.value : "", // Handle null case
                          },
                        });

                        // Reset attendance array when academic year changes
                        setAttendance([]);
                      }}
                      value={
                        sections.find(
                          (section) => section._id === formData.sectionId
                        )
                          ? {
                            value: formData.sectionId,
                            label: sections.find(
                              (section) => section._id === formData.sectionId
                            ).sectionName,
                          }
                          : null
                      }
                      placeholder={
                        sections.length === 0
                          ? "No Section"
                          : "Select a Section"
                      }
                      isDisabled={sections.length === 0}
                      isSearchable={true}
                      isClearable={true}
                      styles={customStyles}
                      required={sections.length > 0}
                    />
                  </FormGroup>
                </Col>
                {formData.reportType !== "month_wise" && (
                  <Col xs={12} sm={6} md={4} xl={3}>
                    <FormGroup>
                      <Label for="attendanceDate">
                        <span style={{ fontWeight: "bold" }}>
                          Attendance Date*
                        </span>
                      </Label>
                      <Input
                        type="date"
                        name="attendanceDate"
                        id="attendanceDate"
                        value={formData.attendanceDate}
                        onChange={handleChange}
                        required
                      />
                    </FormGroup>
                  </Col>
                )}

                {formData.reportType !== "day_wise" && (
                  <>
                    <Col xs={12} sm={6} md={4} xl={3}>
                      <FormGroup>
                        <Label>
                          <strong>Year*</strong>
                        </Label>
                        <Select
                          name="years"
                          options={yearsOptions}
                          value={
                            yearsOptions?.find(
                              (y) => y.value === formData.years
                            ) || null
                          }
                          onChange={handleSelectChange}
                          isClearable
                          placeholder="Select Year"
                          styles={customStyles}
                          required
                        />
                      </FormGroup>
                    </Col>
                    <Col xs={12} sm={6} md={4} xl={3}>
                      <FormGroup>
                        <Label>
                          <strong>Month*</strong>
                        </Label>
                        <Select
                          name="month"
                          options={monthOptions}
                          value={
                            monthOptions.find(
                              (m) => m.value === formData.month
                            ) || null
                          }
                          onChange={handleSelectChange}
                          isClearable
                          placeholder="Select Month"
                          styles={customStyles}
                          required
                        />
                      </FormGroup>
                    </Col>
                  </>
                )}
                <Col xs={12} sm={6} md={4} xl={3}>
                  <div className="mt-sm-4 mb-2 pt-2">
                    <Button type="submit" color="primary" disabled={loading}>
                      {loading ? <Spinner size="sm" /> : "Search"}
                    </Button>
                  </div>
                </Col>
              </Row>
            </Form>
          </div>
        )}
      </Card>

      <Card
        style={{
          boxShadow: "none",
          marginTop: "0px",
          paddingTop: "0px",
          minHeight: "330px",
        }}
        className="px-3 mt-1 mb-4"
      >
        {attendance && attendance.length > 0 ? (
          renderTable()
        ) : (
          <div style={{ marginTop: "120px" }} className="text-center">
            <BiSearch style={{ fontSize: '50px' }} className="text-2xl" />
            <h5>Select the above criteria and click search to view attendance.</h5>
          </div>

        )}
      </Card>
    </>
  );
};

export default Attendance;
